import { Zap, Target, Shield, CalendarDays } from 'lucide-react';
import { Property, PricingRule, AvailabilityRule } from '@/types/revenue-manager';
import { useCurrencyFormat } from '@/hooks/use-currency-format';

interface StatsCardsProps {
  property: Property;
  rules: PricingRule[];
  availabilityRules: AvailabilityRule[];
  upcomingEventsCount: number;
}

export function StatsCards({ property, rules, availabilityRules, upcomingEventsCount }: StatsCardsProps) {
  const activeRules = rules.filter((r) => r.isActive);
  const activeAvailabilityRules = availabilityRules.filter((r) => r.isActive);
  const { format } = useCurrencyFormat();

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {/* Pricing Rules */}
      <div className="stat-card animate-fade-in">
        <div className="flex items-start justify-between">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
            <Zap className="h-5 w-5 text-muted-foreground" />
          </div>
          <span className="status-badge status-active">Active</span>
        </div>
        <div className="mt-4">
          <p className="stat-label">Pricing Rules</p>
          <p className="stat-value">
            {activeRules.length}
            <span className="ml-1 text-base font-normal text-muted-foreground">
              / {rules.length}
            </span>
          </p>
        </div>
      </div>

      {/* Base Price */}
      <div className="stat-card animate-fade-in" style={{ animationDelay: '50ms' }}>
        <div className="flex items-start justify-between">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
            <Target className="h-5 w-5 text-muted-foreground" />
          </div>
          <span className="status-badge status-base">Base</span>
        </div>
        <div className="mt-4">
          <p className="stat-label">Base Price</p>
          <p className="stat-value">
            {format(property.basePrice)}
            <span className="ml-1 text-base font-normal text-muted-foreground">/ night</span>
          </p>
        </div>
      </div>

      {/* Availability */}
      <div className="stat-card animate-fade-in" style={{ animationDelay: '100ms' }}>
        <div className="flex items-start justify-between">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
            <Shield className="h-5 w-5 text-muted-foreground" />
          </div>
          <span className="status-badge status-protect">Protect</span>
        </div>
        <div className="mt-4">
          <p className="stat-label">Availability</p>
          <p className="stat-value">
            {activeAvailabilityRules.length}
            <span className="ml-1 text-base font-normal text-muted-foreground">
              / {availabilityRules.length}
            </span>
          </p>
        </div>
      </div>

      {/* Events */}
      <div className="stat-card animate-fade-in" style={{ animationDelay: '150ms' }}>
        <div className="flex items-start justify-between">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
            <CalendarDays className="h-5 w-5 text-muted-foreground" />
          </div>
          <span className="status-badge status-upcoming">Upcoming</span>
        </div>
        <div className="mt-4">
          <p className="stat-label">Events</p>
          <p className="stat-value">
            {upcomingEventsCount}
            <span className="ml-1 text-base font-normal text-muted-foreground">scheduled</span>
          </p>
        </div>
      </div>
    </div>
  );
}
